import React from 'react';
import Card from './Card';

interface FeatureCardProps {
  /**
   * Feature title
   */
  title: string;
  /**
   * Feature description
   */
  description: string;
  /**
   * Optional icon component or name
   */
  icon?: React.ReactNode | string;
  /**
   * Optional additional CSS classes
   */
  className?: string;
  /**
   * Whether to show hover effects
   */
  hover?: boolean;
  /**
   * Optional list of feature details
   */
  details?: string[];
}

/**
 * Component for displaying feature highlights in a card format
 */
const FeatureCard: React.FC<FeatureCardProps> = ({
  title,
  description,
  icon,
  className = '',
  hover = true,
  details,
}) => {
  // Generic SVG icon if no specific icon is provided
  const defaultIcon = (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  return (
    <Card hover={hover} className={`p-6 h-full ${className}`}>
      <div className="flex flex-col h-full">
        {/* Icon */}
        <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
          {typeof icon === 'string' ? (
            // If icon is a string, assume it's an icon name and render a placeholder
            defaultIcon
          ) : (
            // Otherwise use the React node directly
            icon || defaultIcon
          )}
        </div>
        
        {/* Content */}
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-neutral-600 dark:text-neutral-300">{description}</p>

        {/* Details list if provided */}
        {details && details.length > 0 && (
          <ul className="mt-4 space-y-2">
            {details.map((detail, index) => (
              <li key={index} className="flex items-start">
                <svg className="w-4 h-4 text-primary mt-1 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm">{detail}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
};

export default FeatureCard;